import { useMemo, useState } from "react";

import EstimateTable from "../../../estimate/components/EstimateTable";
import EstimateSidebar from "../../../estimate/components/EstimateSidebar";
import { buildEstimateViewModel } from "../../../estimate/utils/buildEstimateViewModel";
import { groupEstimateRows } from "../../../../features/quote/utils/groupEstimateRows";
import { isInactiveEstimateRow } from "../../../../pricing/calculateEstimateTotals";

function sortRows(rows = []) {
  return [...rows].sort((a, b) => Number(b.total || 0) - Number(a.total || 0));
}

function getProjectTitle(estimate = {}) {
  const projectType =
    estimate?.meta?.parsedProjectType || estimate?.meta?.calculator || "project";

  const projectTitleMap = {
    external_loadbearing_wall: "External walls",
  };

  return projectTitleMap[projectType] || "Project";
}

function bucketRows(rows = []) {
  const active = [];
  const inactive = [];

  for (const row of rows) {
    if (isInactiveEstimateRow(row)) inactive.push(row);
    else active.push(row);
  }

  return { active, inactive };
}

function buildGroupedSections(estimate = {}) {
  const allRows = [
    ...(estimate.materials || []).map((row) => ({ ...row, type: "material" })),
    ...(estimate.labour || []).map((row) => ({ ...row, type: "labour" })),
    ...(estimate.extras || []).map((row) => ({ ...row, type: "extra" })),
  ];

  const { active, inactive } = bucketRows(allRows);

  const groupedActive = sortRows(groupEstimateRows(active));
  const groupedInactive = sortRows(groupEstimateRows(inactive));

  const sections = [];

  if (groupedActive.length > 0) {
    sections.push({
      key: "active",
      title: "Active",
      rows: groupedActive,
    });
  }

  if (groupedInactive.length > 0) {
    sections.push({
      key: "inactive",
      title: "Inactive",
      rows: groupedInactive,
      variant: "optional",
    });
  }

  return [
    {
      key: "project",
      title: getProjectTitle(estimate),
      sections,
    },
  ];
}

export default function EstimateWorkspace({
  estimate,
  parsedData,
  onRebuild,
  onOpenBoom,
  onSave,
  onUndo,
  canUndo,
  isBusy,
  onToggleRowIncluded,
  onChangeMarginPercent,
}) {
  const [showBreakdown, setShowBreakdown] = useState(false);

  const viewModel = useMemo(
    () => buildEstimateViewModel(estimate, parsedData),
    [estimate, parsedData]
  );

  const groupedSections = useMemo(
    () => buildGroupedSections(estimate),
    [estimate]
  );

  const sectionsToShow = showBreakdown ? viewModel.sections : groupedSections;

  return (
    <div className="grid h-full grid-cols-[minmax(0,1fr)_340px] bg-[#f3f2f1] text-[#201f1e]">
      <main className="min-w-0 overflow-hidden border-r border-[#edebe9] bg-white">
        <div className="flex h-10 items-center justify-between border-b border-[#edebe9] bg-[#faf9f8] px-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowBreakdown((value) => !value)}
              className="rounded-sm px-3 py-1.5 text-sm text-[#323130] hover:bg-[#f3f2f1]"
            >
              {showBreakdown ? "Grouped" : "Breakdown"}
            </button>

            <button
              type="button"
              onClick={onSave}
              disabled={isBusy}
              className="rounded-sm px-3 py-1.5 text-sm font-medium text-[#201f1e] hover:bg-[#f3f2f1] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save
            </button>

            {onUndo && (
              <button
                type="button"
                onClick={onUndo}
                disabled={!canUndo || isBusy}
                className="rounded-sm px-3 py-1.5 text-sm text-[#323130] hover:bg-[#f3f2f1] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Undo
              </button>
            )}
          </div>
        </div>

        <div className="h-[calc(100%-40px)] overflow-auto bg-[#f3f2f1]">
          <EstimateTable
            sections={sectionsToShow}
            onToggleRowIncluded={onToggleRowIncluded}
          />
        </div>
      </main>

      <aside className="overflow-auto border-l border-[#edebe9] bg-[#f3f2f1]">
        <EstimateSidebar
          estimate={estimate}
          summary={viewModel.summary}
          review={viewModel.review}
          onRebuild={onRebuild}
          onOpenBoom={onOpenBoom}
          onSave={onSave}
          onChangeMarginPercent={onChangeMarginPercent}
        />
      </aside>
    </div>
  );
}